import { Button, MindyLogoDark, MindyLogoLight, useTheme } from '@quadminds/mindkit';
import { CORS_WHITELIST } from '@quadminds/shared/commons';
import { isProduction } from '@quadminds/shared/utils';
import { motion } from 'framer-motion';
import Lottie from 'lottie-react';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import animationData from '../../../../assets/json/loader.json';
import { MINDY_APP_URL } from '../../../../config';
import { SENDER } from '../../../../enums/sender.enum';
import { SOURCE } from '../../../../enums/source.enum';
import useChat from '../../../../hooks/useChat';
import AnimatedDots from '../../../common/AnimatedDots/AnimatedDots';
import { Response } from '../../Chat/Messages/Response/Response';
import { ThinkingLoader } from '../../Chat/ThinkingLoader';
import { Input } from '../Input/Input';
import { UserMessage } from '../Messages/User/User';

export function Embedded() {
    const { theme, setTheme } = useTheme();
    const [expanded, setExpanded] = useState(false);
    const [newHeader] = useState(() => new URLSearchParams(globalThis.location.search).get('new_header') === 'true');

    const {
        chatContainerRef,
        inputContainerRef,
        firebaseIdToken,
        userInfo,
        conversationId,
        ref,
        inputRef,
        chatInputValue,
        setChatInputValue,
        files,
        setFiles,
        messages,
        setMessages,
        handleSendMessage,
        isLoading,
        isStreaming,
        mode,
        setConversationId,
        agentVersion,
        progressiveThinkingMessage,
    } = useChat();

    useEffect(() => {
        // El embed histórico ignora ?theme= y siempre fuerza dark; se mantiene así
        // para no correr el tema por debajo de los usuarios actuales. La variante
        // new_header sí respeta el claro, que es como se ve en la foto de referencia.
        setTheme(newHeader ? 'light' : 'dark');
    }, [setTheme, newHeader]);

    const handleClose = useCallback(() => {
        (document.activeElement as HTMLElement)?.blur();
        setChatInputValue('');
        setFiles([]);
        setExpanded(false);
    }, [setChatInputValue, setFiles, setExpanded]);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data.type === 'MINDY_CLOSE') {
                handleClose();
            }
        };

        globalThis.addEventListener('message', handleMessage);

        return () => {
            globalThis.removeEventListener('message', handleMessage);
        };
    }, [handleClose]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && expanded) {
                handleClose();
            }
        };

        globalThis.addEventListener('keydown', handleKeyDown);

        return () => {
            globalThis.removeEventListener('keydown', handleKeyDown);
        };
    }, [expanded, handleClose]);

    const getTargetOrigin = () => {
        const referrer = document.referrer;
        if (!referrer) return null;
        try {
            const origin = new URL(referrer).origin;
            const isWhitelisted = CORS_WHITELIST.some((allowed) => {
                if (allowed.startsWith('http')) return allowed === origin;
                return origin.includes(allowed);
            });
            return isWhitelisted ? origin : null;
        } catch {
            return null;
        }
    };

    useEffect(() => {
        const targetOrigin = getTargetOrigin();
        if (!targetOrigin) return;

        if (expanded) {
            globalThis.parent.postMessage({ type: 'MINDY_EXPAND', height: 600 }, targetOrigin);
        } else {
            globalThis.parent.postMessage({ type: 'MINDY_COLLAPSE', height: 50 }, targetOrigin);
        }
    }, [expanded]);

    const handleSendMessageWithExpand = () => {
        if (!expanded) {
            setExpanded(true);
        }
        handleSendMessage({ mode, source: SOURCE.EMBEDDED });
    };

    const handleInputFocus = () => {
        if (!expanded && messages.length > 0) {
            setExpanded(true);
        }
    };

    // Scroll to bottom when chat expands and there are messages
    useEffect(() => {
        if (expanded && messages.length > 0 && chatContainerRef.current) {
            setTimeout(() => {
                chatContainerRef.current?.scrollTo({
                    top: chatContainerRef.current.scrollHeight,
                    behavior: 'smooth',
                });
            }, 100);
        }
    }, [expanded, chatContainerRef, messages.length]);

    const handleContinueInMindy = () => {
        if (conversationId) {
            const baseUrl = MINDY_APP_URL || '/apps/mindy-app';
            const token = localStorage.getItem('token');
            const tokenParam = token ? `?token=${token}` : '';
            globalThis.open(`${baseUrl}/chat/${conversationId}${tokenParam}`, '_blank');
        }

        (document.activeElement as HTMLElement)?.blur();
        setMessages([]);
        setChatInputValue('');
        setFiles([]);
        setConversationId(undefined);
        setExpanded(false);
    };

    if (!userInfo) {
        return (
            <div className="flex items-center justify-center w-full h-full bg-background">
                <div className="h-36 w-36">
                    <Lottie animationData={animationData} loop={true} />
                </div>
            </div>
        );
    }

    return (
        <section
            ref={chatContainerRef}
            className={`flex relative flex-col w-full h-full self-center overflow-x-hidden ${
                expanded ? 'space-y-4 px-8 pt-16 pb-40' : ''
            }`}
        >
            {expanded && (
                <>
                    <div className="transition-transform animate-in fade-in duration-700 ease-in fixed top-0 left-0 flex w-full bg-background z-10">
                        <div className="flex items-center w-full px-6 pt-6 pb-4 justify-between">
                            <img
                                src={theme === 'dark' ? MindyLogoLight : MindyLogoDark}
                                alt="Mindy"
                                className="h-8 mr-2"
                            />
                            <div className="flex items-center gap-2">
                                <Button
                                    type="button"
                                    onClick={handleContinueInMindy}
                                    disabled={isLoading || isStreaming}
                                >
                                    Continuar en Mindy
                                </Button>
                                <Button type="button" variant="outline" onClick={handleClose}>
                                    Cerrar
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col flex-grow relative">
                        <motion.div
                            className="flex flex-col space-y-12 flex-grow"
                            animate={{ justifyContent: messages.length > 0 ? 'flex-end' : 'flex-start' }}
                            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <div ref={ref} data-testid="chat-messages">
                                {messages.length > 0 && (
                                    <div className="space-y-6">
                                        {messages.map((msg, index) => {
                                            const isLastAgentMessage =
                                                msg.sender !== SENDER.user && index === messages.length - 1;

                                            return (
                                                <div id={`message-${index}`} key={`message-${index}`}>
                                                    {msg.sender === SENDER.user ? (
                                                        <UserMessage
                                                            text={msg.text}
                                                            files={msg.files || []}
                                                            interactionId={msg.interactionId}
                                                            chatMode={mode}
                                                        />
                                                    ) : (
                                                        <Response
                                                            id={msg.interactionId as string}
                                                            interactionId={msg.interactionId}
                                                            ratingMsg={msg.rating}
                                                            text={msg.text}
                                                            animated={isLastAgentMessage}
                                                            messageType={msg.messageType}
                                                            attachment={msg?.attachments}
                                                            mailToken={firebaseIdToken}
                                                            mail={userInfo?.email}
                                                            payload={{
                                                                user_email: userInfo?.email || '',
                                                                feedback_date: moment().format('DD-MM-YYYY HH:mm:ss'),
                                                                conversation_id: conversationId || '',
                                                                previous_messages: messages
                                                                    .slice(0, index + 1)
                                                                    .map((m, mIndex, arr) => ({
                                                                        is_user: m.sender === SENDER.user,
                                                                        message: m.text,
                                                                        is_flagged: mIndex === arr.length - 1,
                                                                    })),
                                                                user_comments: '',
                                                                is_impersonating:
                                                                    userInfo?.originalLoginId !== undefined &&
                                                                    userInfo.originalLoginId !== userInfo.externalId,
                                                                original_user_id: userInfo?.originalLoginId,
                                                                organization:
                                                                    userInfo?.organizations &&
                                                                    userInfo?.currentOrganization &&
                                                                    userInfo.organizations.some(
                                                                        (org) =>
                                                                            org.id === userInfo.currentOrganization,
                                                                    )
                                                                        ? userInfo.organizations.find(
                                                                              (org) =>
                                                                                  org.id ===
                                                                                  userInfo.currentOrganization,
                                                                          )?.name || ''
                                                                        : '',
                                                                typology: userInfo?.typology || '',
                                                                environment: isProduction() ? 'Producción' : 'Pruebas',
                                                                agent_version: agentVersion,
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                {isLoading &&
                                    messages.length > 0 &&
                                    messages[messages.length - 1]?.sender === SENDER.user && (
                                        <motion.div
                                            className="px-2 self-start"
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <div className="flex items-center gap-3 py-2">
                                                <ThinkingLoader />
                                                <span className="animate-pulse italic">
                                                    {progressiveThinkingMessage.endsWith('...') ? (
                                                        <>
                                                            {progressiveThinkingMessage.slice(0, -3)}
                                                            <AnimatedDots />
                                                        </>
                                                    ) : (
                                                        progressiveThinkingMessage
                                                    )}
                                                </span>
                                            </div>
                                        </motion.div>
                                    )}
                            </div>
                        </motion.div>
                    </div>
                </>
            )}

            <div
                ref={inputContainerRef}
                className={`transition-transform animate-in fade-in duration-700 ease-in fixed bottom-0`}
            >
                <div className={`bg-background ${expanded ? 'px-6 pb-6 pt-4' : ''}`}>
                    <Input
                        data-testid="chat-input"
                        ref={inputRef}
                        chatInputValue={chatInputValue}
                        setChatInputValue={setChatInputValue}
                        files={files}
                        setFiles={setFiles}
                        onSendMessage={handleSendMessageWithExpand}
                        onFocus={handleInputFocus}
                        isLoading={isLoading}
                        expanded={expanded}
                        newHeader={newHeader}
                    />
                </div>
            </div>
        </section>
    );
}

export default Embedded;
